<template>
  <BaseInnerLoading v-if="isRetrievingUser" />

  <div
    v-else-if="isInviteTokenInvalid"
    class="space-y-4 py-8 text-center"
  >
    <div class="flex justify-center">
      <IconBeepaidCustomers />
    </div>

    <div class="space-y-2">
      <p class="text-xl font-semibold text-error">
        Invalid invitation
      </p>
      <p class="text-sm text-background-fg/70">
        Your invite token is invalid, please ask your
        administrator to send a new one.
      </p>
    </div>

    <RouterLink
      :to="{ name: 'login' }"
      class="inline-flex text-sm font-medium text-primary underline"
    >
      Return to login
    </RouterLink>
  </div>

  <div
    v-else-if="retrievalError"
    class="space-y-4 py-8 text-center"
  >
    <p class="text-xl font-semibold text-error">
      We couldn't load this invitation.
    </p>
    <p class="text-sm text-background-fg/70">
      Please ask your administrator to send a new
      invitation.
    </p>
  </div>

  <form
    v-else-if="inviteLoaded"
    class="space-y-6"
    @submit="onSubmit"
  >
    <div class="flex justify-center p-4">
      <IconBeepaidCustomers />
    </div>

    <FormInputText name="firstName" label="First Name" />

    <FormInputText name="lastName" label="Last Name" />

    <FormInputText name="email" label="Email" disabled />

    <FormInputText name="phone" label="Phone Number" />

    <FormInputPassword name="password" label="Password" />
    <FormInputPassword
      name="passwordConfirm"
      label="Confirm Password"
    />

    <FormInputCheckbox name="terms">
      <template #label>
        By checking this you confirm that you have read and
        agree to the
        <RouterLink
          :to="{ name: 'termsAndConditions' }"
          class="text-primary"
        >
          Terms and Conditions
        </RouterLink>
      </template>
    </FormInputCheckbox>

    <FormSubmitButton
      class="w-full"
      :is-loading="isSubmitting"
    >
      Create Account
    </FormSubmitButton>
  </form>
</template>

<script setup lang="ts">
import * as z from "zod"
import { useForm } from "vee-validate"
import { toTypedSchema } from "@vee-validate/zod"
import { ApiError } from "fetch-plus"

const props = defineProps<{
  inviteToken: string
}>()

const router = useRouter()

const createEditUserFormSchema = z
  .object({
    firstName: z
      .string()
      .min(1, "First name must not be empty")
      .max(150, "First name too long"),
    lastName: z
      .string()
      .min(1, "Last name must not be empty")
      .max(150, "Last name too long"),
    email: z
      .string()
      .email("Please enter a valid email address"),
    phone: z.string().nullable(),
    password: z
      .string()
      .min(1, "Password must not be empty"),
    passwordConfirm: z.string(),
    terms: z.boolean(),
  })
  .superRefine((params, ctx) => {
    if (params.password !== params.passwordConfirm) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords must match",
        path: ["passwordConfirm"],
      })
    }

    if (params.terms !== true) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please accept the terms and conditions",
        path: ["terms"],
      })
    }
  })

const { handleSubmit, isSubmitting, resetForm, setErrors } =
  useForm({
    validationSchema: toTypedSchema(
      createEditUserFormSchema
    ),
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      passwordConfirm: "",
      terms: false,
    },
  })

const isInviteTokenInvalid = ref(false)
const retrievalError = ref(false)
const inviteLoaded = ref(false)

const setInviteTokenInvalid = (error: ApiError) => {
  if (error.code === "invalid-invite-token") {
    isInviteTokenInvalid.value = true
    return true
  }

  return false
}

const onSubmit = handleSubmit(async (params) => {
  try {
    isInviteTokenInvalid.value = false
    await acceptInvite(props.inviteToken, params)
    toast.success("User Created")
    router.push({
      name: "home",
    })
  } catch (error) {
    if (error instanceof ApiError) {
      if (setInviteTokenInvalid(error)) {
        return
      }

      if (error.requestErrors?.body) {
        setErrors(error.requestErrors?.body ?? {})
        toast.error(
          "We couldn't create your account. Please check the highlighted fields."
        )
        return
      }

      toast.error(
        error.detail ??
          "We couldn't create your account right now."
      )
      return
    }

    toast.error(
      "We couldn't create your account right now."
    )
  }
})

const isRetrievingUser = ref(false)
onMounted(async () => {
  try {
    isRetrievingUser.value = true
    isInviteTokenInvalid.value = false
    retrievalError.value = false

    resetForm({
      values: await retrieveInvite(props.inviteToken),
    })
    inviteLoaded.value = true
  } catch (error) {
    if (error instanceof ApiError) {
      if (setInviteTokenInvalid(error)) {
        return
      }

      retrievalError.value = true
    } else {
      retrievalError.value = true
    }
  } finally {
    isRetrievingUser.value = false
  }
})
</script>
